import type { ApiKeyAccess, ApiKeyCreated, ApiKeyView, WebhookView } from '@bb/shared';
import { API_KEY_ACCESS, API_SCOPES, READ_ONLY_SCOPES } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useApiKeys,
  useCreateApiKey,
  useDeleteWebhook,
  useEnableWebhook,
  useRevokeApiKey,
  useWebhooks,
} from '@/features/integrations/api';
import { LineSection } from '@/features/line/line-section';
import { ZoomSection } from '@/features/zoom/zoom-section';
import { ApiRequestError } from '@/lib/api';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  btnSmall,
  cardCls,
  inputCls,
  labelCls,
} from '@/lib/ui';

const TABS = ['line', 'zoom', 'apiKeys', 'webhooks'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/app/settings/integrations')({
  /*
    The tab lives in the URL so it can be linked to and survives a reload. That
    matters more here than on most screens: LINE setup is done with the LINE
    console open in another tab, so a shop bounces between the two repeatedly
    and should not land back on a different section each time.
  */
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    typeof s.tab === 'string' && (TABS as readonly string[]).includes(s.tab)
      ? { tab: s.tab as Tab }
      : {},
  component: IntegrationsPage,
});

/**
 * What each preset grants, from the same constants the API resolves it with — so
 * the screen cannot drift from what the key will actually carry.
 */
const SCOPES_FOR: Record<ApiKeyAccess, readonly string[]> = {
  read_only: READ_ONLY_SCOPES,
  full: API_SCOPES,
};

function IntegrationsPage() {
  const { t } = useTranslation(['account', 'common']);
  const { tab = 'line' } = Route.useSearch();
  return (
    <div className="space-y-6">
      <p className="text-sm text-neutral-600">{t('account:integrations.body')}</p>

      <nav className="-mx-4 flex gap-4 overflow-x-auto border-b border-neutral-200 px-4 text-sm [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        {TABS.map((key) => (
          <Link
            key={key}
            to="/app/settings/integrations"
            search={{ tab: key }}
            replace
            className={`-mb-px flex min-h-11 shrink-0 items-center border-b-2 ${
              tab === key
                ? 'border-neutral-900 font-medium text-neutral-900'
                : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}
          >
            {t(`account:integrations.tabs.${key}`)}
          </Link>
        ))}
      </nav>

      {tab === 'line' ? <LineSection /> : null}
      {tab === 'zoom' ? <ZoomSection /> : null}
      {tab === 'apiKeys' ? <ApiKeysSection /> : null}
      {tab === 'webhooks' ? <WebhooksSection /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------

function ApiKeysSection() {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const { data, isLoading } = useApiKeys();
  const create = useCreateApiKey();
  const [name, setName] = useState('');
  const [access, setAccess] = useState<ApiKeyAccess>('read_only');
  const [error, setError] = useState<string | null>(null);
  /**
   * The new key's secret, held here and nowhere else. It is deliberately not in
   * the query cache: the server will never send it again, so a copy that
   * survives a remount would reappear on a screen that was not asking for it.
   */
  const [fresh, setFresh] = useState<ApiKeyCreated | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) return;
    create.mutate(
      { name: name.trim(), access },
      {
        onSuccess: (created) => {
          setFresh(created);
          setName('');
          setAccess('read_only');
        },
        onError: (err) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
      },
    );
  };

  return (
    <section className="space-y-3">
      <p className="text-sm text-neutral-600">{t('account:integrations.keys.body')}</p>

      {fresh ? <FreshKey created={fresh} onDismiss={() => setFresh(null)} /> : null}

      {isLoading ? (
        <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>
      ) : (data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-neutral-500">{t('account:integrations.keys.none')}</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
          {data?.items.map((key) => (
            <ApiKeyRow key={key.id} apiKey={key} />
          ))}
        </ul>
      )}

      <form onSubmit={submit} className={`${cardCls} space-y-3`}>
        <div>
          <label htmlFor="key-name" className={labelCls}>
            {t('account:integrations.keys.nameLabel')}
          </label>
          <input
            id="key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder={t('account:integrations.keys.namePlaceholder')}
            autoComplete="off"
            className={inputCls}
          />
          <p className="mt-1 text-xs text-neutral-500">{t('account:integrations.keys.nameHelp')}</p>
        </div>

        <fieldset className="space-y-2">
          <legend className={labelCls}>{t('account:integrations.keys.accessLabel')}</legend>
          {API_KEY_ACCESS.map((option) => (
            <label key={option} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="access"
                value={option}
                checked={access === option}
                onChange={() => setAccess(option)}
                className="mt-1 size-4"
              />
              <span>
                {t(`account:integrations.keys.access.${option}.label`)}
                <span className="mt-0.5 block text-xs text-neutral-500">
                  {t(`account:integrations.keys.access.${option}.help`)}
                </span>
                {/*
                  The scope names, verbatim. The API refuses a call with
                  "this token is missing the bookings:write scope", and without
                  these that message names something the person has never seen.
                */}
                <code className="mt-1 block font-mono text-xs text-neutral-400">
                  {SCOPES_FOR[option].join('  ')}
                </code>
              </span>
            </label>
          ))}
        </fieldset>

        {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
        <button
          type="submit"
          disabled={name.trim().length === 0 || create.isPending}
          className={btnPrimary}
        >
          {t('account:integrations.keys.create')}
        </button>
      </form>
    </section>
  );
}

/**
 * The one screen the secret ever appears on.
 *
 * It stays until dismissed rather than fading, because the next thing the
 * person has to do is paste it somewhere else, and a message that disappears
 * while they are switching windows means minting a second key.
 */
function FreshKey({ created, onDismiss }: { created: ApiKeyCreated; onDismiss: () => void }) {
  const { t } = useTranslation(['account', 'common']);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      // No clipboard permission, or an insecure context. The key is on screen
      // and selectable, so there is nothing to recover from.
      setCopied(false);
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-green-300 bg-green-50 p-4">
      <p className="text-sm font-medium text-green-900">
        {t('account:integrations.keys.created', { name: created.name })}
      </p>
      <p className="text-sm text-green-900">{t('account:integrations.keys.copyNow')}</p>
      <code className="block overflow-x-auto rounded border border-green-300 bg-white px-3 py-2 font-mono text-xs">
        {created.token}
      </code>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} className={btnSecondary}>
          {copied ? t('common:status.copied') : t('common:actions.copy')}
        </button>
        <button type="button" onClick={onDismiss} className={btnSecondary}>
          {t('account:integrations.keys.dismiss')}
        </button>
      </div>
    </div>
  );
}

function ApiKeyRow({ apiKey }: { apiKey: ApiKeyView }) {
  const { t } = useTranslation(['account', 'common']);
  const revoke = useRevokeApiKey();
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{apiKey.name}</p>
        <p className="mt-0.5 font-mono text-xs text-neutral-500">bbk_{apiKey.prefix}…</p>
        <p className="mt-1 text-xs text-neutral-500">
          {t(`account:integrations.keys.access.${apiKey.access}.label`)}
          {' · '}
          {apiKey.lastUsedAt
            ? t('account:integrations.keys.lastUsed', {
                date: new Date(apiKey.lastUsedAt).toLocaleDateString(),
              })
            : t('account:integrations.keys.neverUsed')}
        </p>
        {/* What the key can actually do, in the words the API's errors use. */}
        <code className="mt-1 block font-mono text-xs break-words text-neutral-400">
          {apiKey.scopes.join('  ')}
        </code>
      </div>
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => revoke.mutate(apiKey.id)}
            disabled={revoke.isPending}
            className={btnDanger}
          >
            {t('account:integrations.keys.confirmRevoke')}
          </button>
          <button type="button" onClick={() => setConfirming(false)} className={btnSmall}>
            {t('common:actions.cancel')}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)} className={btnSmall}>
          {t('account:integrations.keys.revoke')}
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

function WebhooksSection() {
  const { t } = useTranslation(['account', 'common']);
  const { data, isLoading } = useWebhooks();
  return (
    <section className="space-y-3">
      <p className="text-sm text-neutral-600">{t('account:integrations.webhooks.body')}</p>
      {isLoading ? (
        <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>
      ) : (data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-neutral-500">{t('account:integrations.webhooks.none')}</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
          {data?.items.map((hook) => (
            <WebhookRow key={hook.id} hook={hook} />
          ))}
        </ul>
      )}
    </section>
  );
}

function WebhookRow({ hook }: { hook: WebhookView }) {
  const { t } = useTranslation(['account', 'common']);
  const enable = useEnableWebhook();
  const remove = useDeleteWebhook();
  const [confirming, setConfirming] = useState(false);
  const disabled = hook.status === 'disabled';
  return (
    <li className="space-y-2 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs">{hook.url}</p>
          <p className="mt-1 text-xs text-neutral-500">
            {/* `*` is a real subscription value, not a placeholder. */}
            {hook.events.includes('*')
              ? t('account:integrations.webhooks.allEvents')
              : hook.events.join(', ')}
            {' · '}
            {t('account:integrations.webhooks.createdVia', { via: viaLabel(hook.createdVia) })}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {t(`account:integrations.webhooks.status.${hook.status}`)}
        </span>
      </div>

      {/*
        Why it stopped, in the host's terms. Without this the only thing they
        can see is that an integration went quiet, and the answer — a 404 from
        a URL somebody changed — lives in a log they have no access to.
      */}
      {disabled ? (
        <p className="text-xs text-red-700">
          {t('account:integrations.webhooks.disabledHelp', {
            count: hook.consecutiveFailures,
          })}
        </p>
      ) : null}
      {hook.lastAttempt && !hook.lastAttempt.error && hook.lastAttempt.statusCode ? (
        <p className="text-xs text-neutral-500">
          {t('account:integrations.webhooks.lastAttempt', {
            status: hook.lastAttempt.statusCode,
            date: new Date(hook.lastAttempt.at).toLocaleString(),
          })}
        </p>
      ) : hook.lastAttempt?.error ? (
        <p className="text-xs text-neutral-500">
          {t('account:integrations.webhooks.lastError', {
            error: hook.lastAttempt.error,
            date: new Date(hook.lastAttempt.at).toLocaleString(),
          })}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {disabled ? (
          <button
            type="button"
            onClick={() => enable.mutate(hook.id)}
            disabled={enable.isPending}
            className={btnSmall}
          >
            {t('account:integrations.webhooks.enable')}
          </button>
        ) : null}
        {confirming ? (
          <>
            <button
              type="button"
              onClick={() => remove.mutate(hook.id)}
              disabled={remove.isPending}
              className={btnDanger}
            >
              {t('account:integrations.webhooks.confirmRemove')}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={btnSmall}>
              {t('common:actions.cancel')}
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setConfirming(true)} className={btnSmall}>
            {t('account:integrations.webhooks.remove')}
          </button>
        )}
      </div>
    </li>
  );
}

/** `oauth:<clientId>` is stored; the host wants the platform's name. */
function viaLabel(createdVia: string): string {
  if (!createdVia.startsWith('oauth:')) return 'API key';
  const clientId = createdVia.slice('oauth:'.length);
  return clientId.length > 0 ? clientId : 'OAuth';
}
